import React, { useRef } from "react";
import { Link } from "react-router-dom";
import { Header } from "../components/Header";
import { Footer } from "../components/Footer";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import {
  ArrowRight,
  Award,
  BookOpen,
  GraduationCap,
  Stethoscope,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import Gallery22 from "../images/compressed-images (4)/Gallery22.jpg";

export default function AboutPage() {
  const expertise = [
    {
      icon: Stethoscope,
      title: "Arthroscopic Procedures",
      description:
        "ACL and meniscal repairs, shoulder instability and rotator cuff surgery",
    },
    {
      icon: Award,
      title: "Sports Medicine",
      description:
        "Return-to-sport planning for recreational and competitive athletes",
    },
    {
      icon: BookOpen,
      title: "Fracture Management",
      description: "Complex fracture fixation and post-trauma rehabilitation",
    },
    {
      icon: GraduationCap,
      title: "Patient Education",
      description: "Clear communication and transparent treatment planning",
    },
     {
      icon: Stethoscope,
      title: "Joint Replacement",
      description: "Restoring movement and comfort in damaged joints.",
    },
     {
      icon: Award,
      title: "Hand & Wrist",
      description: "Treating pain and injuries for better hand function.",
    },
     {
      icon: BookOpen,
      title: "Foot & Ankle",
      description: "Healing foot and ankle pain or injuries",
    },
     {
      icon: GraduationCap,
      title: "Knee & Shoulder",
      description: "Expert care for joint pain and injuries",
    },
     {
      icon: Stethoscope,
      title: "Pain & Management",
      description: "Relief for chronic and acute pain.",
    },

  ];

  const scrollRef = useRef(null);

  const scroll = (direction) => {
    if (scrollRef.current) {
      const scrollAmount = 300;
      scrollRef.current.scrollBy({
        left: direction === "left" ? -scrollAmount : scrollAmount,
        behavior: "smooth",
      });
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Header />

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-primary to-primary/90 text-primary-foreground py-16">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="max-w-2xl">
            <h1 className="text-4xl font-bold tracking-tight sm:text-5xl mb-4 font-playfair">
              Meet Dr. Sujit Kumar
            </h1>
            <p className="text-lg text-primary-foreground/90">
              Expert orthopaedic care with a focus on minimally invasive
              techniques and personalized recovery
            </p>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="grid grid-cols-1 gap-12 lg:grid-cols-2 items-start">
            <div>
              <div className="relative h-[350px] md:h-[800px] rounded-lg overflow-hidden shadow-xl mb-8">
                <img
                  src={Gallery22}
                  alt="Dr. V. R. Sujit Kumar"
                  className="object-cover w-full h-full"
                />
              </div>
            </div>

            <div className="space-y-6">
              <div>
                <h2 className="text-3xl font-bold mb-4 font-playfair">
                  Dr. Sujitkumar Vakati R
                </h2>
                <p className="text-lg text-muted-foreground mb-2">
                  Senior Consultant Orthopaedic Surgeon <br />
                  MS (Ortho), MCh (Ortho), AAF (Japan), SEF (Germany), IWC (Paris)<br />
                  FIFA Diploma in Football Medicine<br />

                </p>
              </div>

              <div className="prose prose-lg max-w-none">
                <p className="text-foreground leading-relaxed">
                 Dr. Sujitkumar Vakati R is an internationally trained orthopaedic surgeon specializing in arthroscopy, 
                 joint reconstruction, hand and wrist surgery, and sports injury management.
                He heads the Orthopaedic & Sports Injury Clinic in the Chanda Nagar / Ameenpur area of Hyderabad,
                offering comprehensive care — from precise diagnosis and minimally invasive surgery
                to structured rehabilitation and return-to-sport programs.</p>

                <p className="text-foreground leading-relaxed">
                  His approach blends cutting-edge surgical expertise with a strong emphasis on functional
                   recovery and patient education. Every treatment plan is personalized — ensuring 
                   patients understand their condition, options, and pathway to full recovery.<br />
                  With fellowships and training from Japan, Germany, and Paris,
                   Dr. Sujit brings global standards of orthopaedic excellence
                    to Hyderabad. His special interest areas include:

                </p>

                <p className="text-foreground leading-relaxed pl-10">
                <li> Arthroscopic surgeries for shoulder, knee, and wrist</li>
                 <li>Joint replacement surgeries for knee, shoulder, and elbow</li>
                <li> Hand and wrist trauma, ligament, and reconstructive procedures</li>
                 <li>Sports injury prevention, PRP & regenerative therapies</li>
                <li> Early mobilization and evidence-based rehabilitation</li>
                 
                </p>
              </div>

              <p className="text-foreground leading-relaxed">
                 Whether it’s an athlete aiming to return to peak performance or an individual seeking lasting relief from joint pain, Dr. Sujit focuses on precision care, quicker recovery, and long-term joint health.
              </p>

              

              <div className="pt-4">
                <Button size="lg" asChild>
                  <Link to="/book-appointment">
                    Book Appointment
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Link>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Expertise Section with Scroll Buttons */}
      <section className="py-16 bg-muted/30 relative">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold tracking-tight sm:text-4xl mb-4 font-playfair">
              Areas of Expertise
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Specialized training and experience across multiple aspects of
              orthopaedic care
            </p>
          </div>

          {/* Scroll Buttons */}
          <button
            onClick={() => scroll("left")}
            className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white shadow-md rounded-full p-2"
          >
            <ChevronLeft className="h-6 w-6" />
          </button>
          <button
            onClick={() => scroll("right")}
            className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white shadow-md rounded-full p-2"
          >
            <ChevronRight className="h-6 w-6" />
          </button>

          {/* Scrollable Container */}
          <div
            ref={scrollRef}
            className="flex space-x-6 overflow-x-auto scrollbar-hide pb-4 scroll-smooth"
          >
            {expertise.map((item) => (
              <Card key={item.title} className="min-w-[250px] flex-shrink-0">
                <CardContent className="p-6 text-center">
                  <div className="mb-4 inline-flex h-12 w-12 items-center justify-center rounded-lg bg-secondary/10">
                    <item.icon className="h-6 w-6" />
                  </div>
                  <h3 className="text-lg font-semibold mb-2">{item.title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    {item.description}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="bg-secondary rounded-2xl p-8 md:p-12 text-center text-secondary-foreground">
            <h2 className="text-3xl font-bold mb-4 font-playfair">
              Ready to Discuss Your Treatment?
            </h2>
            <p className="text-lg mb-8 text-secondary-foreground/90 max-w-2xl mx-auto">
              Schedule a consultation to discuss your condition and explore the
              best treatment options for your needs.
            </p>
            <div className="flex items-center justify-center gap-4 flex-wrap">
              <Button
                size="lg"
                variant="secondary"
                className="bg-primary-foreground text-primary hover:bg-primary-foreground/90"
                asChild
              >
                <Link to="/book-appointment">Book Appointment</Link>
              </Button>
              <Button
                size="lg"
                variant="outline"
                className="bg-transparent border-secondary-foreground text-secondary-foreground hover:bg-secondary-foreground hover:text-secondary"
                asChild
              >
                <Link to="/treatments">View Treatments</Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
